import { useEffect, useMemo, useRef, useState } from "react";
import { api, currency, fmtDate } from "@/lib/api";
import { downloadInvoicePdf, generatePdfBase64, makeDocFilename } from "@/lib/pdf";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PackingSlip from "@/components/PackingSlip";
import SigningScreen from "@/components/SigningScreen";
import SignedDeliveryNote from "@/components/SignedDeliveryNote";
import EmailPreviewModal from "@/components/EmailPreviewModal";
import { Eye, Download, PackageCheck, Package, Search, Mail, FilePen, FileSignature, Link2, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "delivered", label: "Delivered" },
  { key: "all", label: "All" },
];

export default function Delivery() {
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [query, setQuery] = useState("");

  // mode: null | "choice" | "download" | "sign" | "signed_view"
  const [mode, setMode] = useState(null);
  const [selectedInv, setSelectedInv] = useState(null);

  // Download (unsigned) dialog
  const [dlNotes, setDlNotes] = useState("");
  const [downloading, setDownloading] = useState(false);

  // Signed view dialog
  const [signedDownloading, setSignedDownloading] = useState(false);

  // Hidden refs for PDF generation (outside any constrained container)
  const packRef = useRef(null);       // unsigned packing slip
  const signedRef = useRef(null);     // signed delivery note view
  const sendPackRef = useRef(null);   // manual send delivery note
  const signedEmailRef = useRef(null); // in-app sign → auto-email

  // Hidden signed note for PDF generation after submit
  const [pendingSignData, setPendingSignData] = useState(null);

  // Manual send dialog
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sending, setSending] = useState(false);

  // Send sign link dialog
  const [signLinkOpen, setSignLinkOpen] = useState(false);
  const [signLinkEmail, setSignLinkEmail] = useState("");
  const [signLinkUrl, setSignLinkUrl] = useState("");
  const [signLinkAppUrlMissing, setSignLinkAppUrlMissing] = useState(false);
  const [sendingSignLink, setSendingSignLink] = useState(false);

  // Post-sign invoice copy prompt (admin)
  const [postSignPromptOpen, setPostSignPromptOpen] = useState(false);
  const [postSignInv, setPostSignInv] = useState(null);
  const [postSignChoice, setPostSignChoice] = useState(""); // "" | "other"
  const [postSignAltEmail, setPostSignAltEmail] = useState("");
  const [autoInvoiceEmailOpen, setAutoInvoiceEmailOpen] = useState(false);
  const [autoInvoiceToEmail, setAutoInvoiceToEmail] = useState("");
  const [autoInvoiceSending, setAutoInvoiceSending] = useState(false);

  const load = async () => {
    const { data } = await api.get("/invoices");
    setInvoices(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = invoices;
    if (filter === "pending") list = invoices.filter((i) => !i.delivered);
    else if (filter === "delivered") list = invoices.filter((i) => i.delivered);
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (inv) =>
        (inv.invoice_number || "").toLowerCase().includes(q) ||
        (inv.customer_name || "").toLowerCase().includes(q) ||
        (inv.order_number || "").toLowerCase().includes(q)
    );
  }, [invoices, filter, query]);

  const openChoice = (inv, e) => {
    if (e) e.stopPropagation();
    setSelectedInv(inv);
    setMode("choice");
  };

  const markDelivered = async (id, delivered, e) => {
    if (e) e.stopPropagation();
    try {
      await api.patch(`/invoices/${id}/delivered`, { delivered });
      toast.success(delivered ? "Marked as delivered" : "Marked as pending delivery");
      load();
    } catch { toast.error("Failed to update delivery status"); }
  };

  const downloadUnsignedPdf = async () => {
    setDownloading(true);
    try {
      const filename = makeDocFilename(selectedInv?.customer_tag, "DeliveryNote", selectedInv?.invoice_number);
      await downloadInvoicePdf(packRef.current, filename, { singlePage: true });
      toast.success("Delivery note downloaded");
    } catch { toast.error("Could not generate delivery note"); }
    finally { setDownloading(false); }
  };

  const downloadSignedPdf = async () => {
    setSignedDownloading(true);
    try {
      const filename = makeDocFilename(selectedInv?.customer_tag, "SignedDeliveryNote", selectedInv?.invoice_number);
      await downloadInvoicePdf(signedRef.current, filename, { singlePage: true });
      toast.success("Signed delivery note downloaded");
    } catch { toast.error("Could not generate PDF"); }
    finally { setSignedDownloading(false); }
  };

  const handleSign = async (sigData) => {
    // 1. Populate the hidden SignedDeliveryNote for PDF generation
    setPendingSignData({ ...sigData, invoice: selectedInv });
    await new Promise((r) => setTimeout(r, 250)); // wait for re-render

    // 2. Generate signed PDF
    let pdf_base64 = null;
    const filename = makeDocFilename(selectedInv?.customer_tag, "SignedDeliveryNote", selectedInv?.invoice_number);
    try {
      if (signedEmailRef.current) {
        pdf_base64 = await generatePdfBase64(signedEmailRef.current, { singlePage: true });
      }
    } catch {
      // PDF generation failed — continue without attachment
    }

    // 3. Post to API (save signature + mark delivered + send email)
    await api.post(`/invoices/${selectedInv.id}/sign`, {
      ...sigData,
      pdf_base64,
      filename,
    });

    toast.success("Delivery signed and saved");
    setPendingSignData(null);
    load();
    setMode(null);
    // Show invoice copy prompt
    setPostSignInv(selectedInv);
    setPostSignChoice("");
    setPostSignAltEmail("");
    setPostSignPromptOpen(true);
  };

  const handleAutoInvoiceSend = async ({ toEmail, subject, customBodyHtml }) => {
    if (!postSignInv?.id) return;
    setAutoInvoiceSending(true);
    try {
      await api.post(`/invoices/${postSignInv.id}/send-auto-invoice`, {
        to_email: toEmail,
        subject,
        custom_body_html: customBodyHtml,
      });
      toast.success(`Invoice sent to ${toEmail}`);
      setAutoInvoiceEmailOpen(false);
      setPostSignPromptOpen(false);
    } catch { toast.error("Failed to send invoice."); }
    finally { setAutoInvoiceSending(false); }
  };

  const openManualSend = (inv, e) => {
    if (e) e.stopPropagation();
    setSelectedInv(inv);
    setSendEmail(inv.customer_email || "");
    setSendOpen(true);
  };

  const openSignLink = async (inv, e) => {
    if (e) e.stopPropagation();
    setSelectedInv(inv);
    setSignLinkEmail(inv.customer_email || "");
    setSignLinkUrl("");
    setSignLinkAppUrlMissing(false);
    setSignLinkOpen(true);
    // Fetch or create sign token + check app_url
    try {
      const { data } = await api.post(`/invoices/${inv.id}/create-sign-link`);
      setSignLinkUrl(data.sign_url || "");
      if (!data.app_url_configured) setSignLinkAppUrlMissing(true);
    } catch {
      toast.error("Could not generate sign link");
      setSignLinkOpen(false);
    }
  };

  const handleSendSignLink = async () => {
    if (!signLinkEmail.trim()) { toast.error("Please enter a recipient email"); return; }
    setSendingSignLink(true);
    try {
      await api.post(`/invoices/${selectedInv.id}/send-sign-link`, { to_email: signLinkEmail.trim() });
      toast.success(`Sign link sent to ${signLinkEmail.trim()}`);
      setSignLinkOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send sign link");
    } finally { setSendingSignLink(false); }
  };

  const handleManualSend = async ({ toEmail, subject, customBodyHtml }) => {
    setSending(true);
    try {
      const filename = makeDocFilename(selectedInv?.customer_tag, "DeliveryNote", selectedInv?.invoice_number);
      let pdf_base64 = null;
      if (sendPackRef.current) pdf_base64 = await generatePdfBase64(sendPackRef.current, { singlePage: true });
      await api.post(`/invoices/${selectedInv.id}/send`, {
        to_email: toEmail, pdf_base64, filename, doc_type: "delivery_note",
        custom_subject: subject, custom_body_html: customBodyHtml,
      });
      toast.success(`Delivery note sent to ${toEmail}`);
      setSendOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send email");
    } finally { setSending(false); }
  };

  // Render full-screen signing overlay
  if (mode === "sign") {
    return (
      <>
        <SigningScreen
          invoice={selectedInv}
          onCancel={() => setMode("choice")}
          onComplete={handleSign}
        />
        {/* Hidden signed note for PDF generation */}
        <div style={{ position: "fixed", left: "-9999px", top: 0, width: "794px", opacity: 0, pointerEvents: "none" }}>
          <div ref={signedEmailRef}>
            {pendingSignData && (
              <SignedDeliveryNote
                invoice={pendingSignData.invoice}
                signatureData={{
                  signed_by: pendingSignData.signed_by,
                  signed_at: pendingSignData.signed_at,
                  signature_base64: pendingSignData.signature_base64,
                  items_confirmed: pendingSignData.items_confirmed,
                }}
              />
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="delivery-page">
      <h1 className="font-heading text-3xl font-bold text-[#032352]">Delivery</h1>
      <p className="text-[#5B5C5F] mt-1 mb-5">
        Delivery notes — view, download, digitally sign, and mark as delivered.
      </p>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="delivery-filter-tabs">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`del-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              filter === f.key
                ? "bg-[#032352] text-white border-[#032352]"
                : "bg-white text-[#5B5C5F] border-slate-200 hover:border-[#032352] hover:text-[#032352]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="relative w-full max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5B5C5F]" />
        <Input
          data-testid="delivery-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by invoice #, customer or order #..."
          className="pl-9"
        />
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-x-auto">
        <Table className="min-w-[660px]">
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead>Invoice #</TableHead>
              <TableHead>Order #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="w-36"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-[#5B5C5F] py-10">
                  No delivery notes found.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((inv) => (
              <TableRow
                key={inv.id}
                data-testid={`delivery-row-${inv.id}`}
                className="cursor-pointer"
                onClick={() => openChoice(inv)}
              >
                <TableCell className="font-mono-num font-medium text-[#032352]">{inv.invoice_number}</TableCell>
                <TableCell className="font-mono-num text-[#5B5C5F] text-sm">{inv.order_number || "—"}</TableCell>
                <TableCell className="text-[#032352]">{inv.customer_name}</TableCell>
                <TableCell className="text-[#5B5C5F] text-sm">{fmtDate(inv.invoice_date)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 flex-wrap">
                    {inv.delivered ? (
                      <Badge className="bg-emerald-500 hover:bg-emerald-500" data-testid={`delivery-status-${inv.id}`}>
                        Delivered
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-slate-500 border-slate-300" data-testid={`delivery-status-${inv.id}`}>
                        Pending
                      </Badge>
                    )}
                    {inv.signature_data && (
                      <Badge className="bg-[#032352] text-white text-xs" data-testid={`signed-badge-${inv.id}`}>
                        <FileSignature className="w-3 h-3 mr-1 inline" />Signed
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono-num text-[#032352]">{currency(inv.total)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-3 justify-end">
                    {!inv.delivered ? (
                      <button
                        data-testid={`mark-delivered-${inv.id}`}
                        onClick={(e) => markDelivered(inv.id, true, e)}
                        title="Mark as delivered"
                        className="text-slate-400 hover:text-emerald-600 transition-colors"
                      >
                        <PackageCheck className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        data-testid={`mark-undelivered-${inv.id}`}
                        onClick={(e) => markDelivered(inv.id, false, e)}
                        title="Mark as pending"
                        className="text-emerald-600 hover:text-amber-600 transition-colors"
                      >
                        <Package className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      data-testid={`send-delivery-note-${inv.id}`}
                      onClick={(e) => openManualSend(inv, e)}
                      title="Email delivery note PDF"
                      className="text-slate-400 hover:text-[#032352] transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`send-sign-link-${inv.id}`}
                      onClick={(e) => openSignLink(inv, e)}
                      title="Send sign link to client"
                      className="text-slate-400 hover:text-[#032352] transition-colors"
                    >
                      <Link2 className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`view-delivery-note-${inv.id}`}
                      onClick={(e) => openChoice(inv, e)}
                      title="View / sign"
                      className="text-slate-400 hover:text-[#032352] transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* ── Choice dialog ── */}
      <Dialog open={mode === "choice"} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent className="max-w-xs" data-testid="delivery-choice-dialog">
          <DialogTitle className="text-[#032352] font-heading font-bold">
            {selectedInv?.invoice_number}
          </DialogTitle>
          <DialogDescription className="text-[#5B5C5F] text-sm">
            {selectedInv?.customer_name}
          </DialogDescription>
          <div className="space-y-3 pt-1">
            {selectedInv?.signature_data && (
              <Button
                onClick={() => setMode("signed_view")}
                data-testid="choice-view-signed"
                className="w-full bg-emerald-500 hover:bg-emerald-600"
              >
                <FileSignature className="w-4 h-4 mr-2" /> View Signed Note
              </Button>
            )}
            <Button
              onClick={() => { setDlNotes(""); setMode("download"); }}
              data-testid="choice-download"
              variant="outline"
              className="w-full border-[#032352] text-[#032352]"
            >
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
            <Button
              onClick={() => { setMode(null); openSignLink(selectedInv); }}
              data-testid="choice-send-sign-link"
              variant="outline"
              className="w-full border-[#032352] text-[#032352]"
            >
              <Link2 className="w-4 h-4 mr-2" /> Send Sign Link
            </Button>
            <Button
              onClick={() => setMode("sign")}
              data-testid="choice-sign"
              className={`w-full ${selectedInv?.signature_data ? "bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200" : "bg-[#032352] hover:bg-[#04347A] text-white"}`}
            >
              <FilePen className="w-4 h-4 mr-2" />
              {selectedInv?.signature_data ? "Re-sign" : "Digitally Sign"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Download (unsigned) dialog ── */}
      <Dialog open={mode === "download"} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="delivery-download-dialog">
          <DialogTitle className="sr-only">Delivery Note</DialogTitle>
          <DialogDescription className="sr-only">Delivery note PDF</DialogDescription>
          <div className="p-4 pb-0 space-y-3">
            <div>
              <Label className="text-xs">Notes (added to the delivery note)</Label>
              <Textarea
                data-testid="delivery-notes-input"
                value={dlNotes}
                onChange={(e) => setDlNotes(e.target.value)}
                placeholder="e.g. Deliver to rear entrance, call on arrival..."
              />
            </div>
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={downloadUnsignedPdf}
                disabled={downloading}
                data-testid="download-delivery-button"
                className="border-[#032352] text-[#032352]"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloading ? "Generating…" : "Download Delivery Note"}
              </Button>
            </div>
          </div>
          {/* Preview only — ref is on hidden element outside dialog */}
          <PackingSlip invoice={selectedInv} notes={dlNotes} />
        </DialogContent>
      </Dialog>

      {/* ── Signed view dialog ── */}
      <Dialog open={mode === "signed_view"} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="signed-view-dialog">
          <DialogTitle className="sr-only">Signed Delivery Note</DialogTitle>
          <DialogDescription className="sr-only">Signed delivery note for {selectedInv?.customer_name}</DialogDescription>
          <div className="p-4 pb-0 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={downloadSignedPdf}
              disabled={signedDownloading}
              data-testid="download-signed-button"
              className="border-[#032352] text-[#032352]"
            >
              <Download className="w-4 h-4 mr-2" />
              {signedDownloading ? "Generating…" : "Download Signed PDF"}
            </Button>
          </div>
          {/* Preview only — ref is on hidden element outside dialog */}
          <SignedDeliveryNote
            invoice={selectedInv}
            signatureData={selectedInv?.signature_data}
          />
        </DialogContent>
      </Dialog>

      {/* ── All hidden off-screen elements for PDF generation ── */}
      <div style={{ position: "fixed", left: "-9999px", top: 0, width: "794px", opacity: 0, pointerEvents: "none", zIndex: -1 }}>
        {/* Unsigned packing slip (download) */}
        <div ref={packRef}>
          <PackingSlip invoice={selectedInv} notes={dlNotes} />
        </div>
        {/* Signed delivery note (view/download) */}
        <div ref={signedRef}>
          <SignedDeliveryNote
            invoice={selectedInv}
            signatureData={selectedInv?.signature_data}
          />
        </div>
        {/* Manual send delivery note */}
        <div ref={sendPackRef}>
          <PackingSlip invoice={selectedInv} notes="" />
        </div>
        {/* In-app signing — auto-email after submit */}
        <div ref={signedEmailRef}>
          {pendingSignData && (
            <SignedDeliveryNote
              invoice={pendingSignData.invoice}
              signatureData={{
                signed_by: pendingSignData.signed_by,
                signed_at: pendingSignData.signed_at,
                signature_base64: pendingSignData.signature_base64,
                items_confirmed: pendingSignData.items_confirmed,
              }}
            />
          )}
        </div>
      </div>

      {/* ── Manual send — Email Preview Modal ── */}
      <EmailPreviewModal
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        title={`Send Delivery Note — ${selectedInv?.invoice_number}`}
        toEmail={sendEmail}
        customerEmail={selectedInv?.customer_email}
        docType="delivery_note"
        invoice={selectedInv}
        attachmentName={selectedInv ? makeDocFilename(selectedInv.customer_tag, "DeliveryNote", selectedInv.invoice_number) : undefined}
        onSend={handleManualSend}
        sending={sending}
      />

      {/* ── Send Sign Link dialog ── */}
      <Dialog open={signLinkOpen} onOpenChange={(o) => { if (!sendingSignLink) setSignLinkOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="send-sign-link-dialog">
          <DialogTitle className="text-[#032352] font-heading font-bold">
            Send Sign Link — {selectedInv?.invoice_number}
          </DialogTitle>
          <DialogDescription className="text-[#5B5C5F] text-sm">
            Client receives an email with a unique link to view and digitally sign their delivery note.
          </DialogDescription>

          {signLinkAppUrlMissing && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
              <strong>App URL not configured.</strong> Go to Settings → App URL to set your deployed domain before sending sign links.
              The link preview below is a relative URL only.
            </div>
          )}

          {signLinkUrl ? (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
              <p className="text-xs text-[#5B5C5F] uppercase tracking-wider mb-1">Sign Link Preview</p>
              <p className="font-mono text-xs text-[#032352] break-all">{signLinkUrl}</p>
              <button
                data-testid="copy-sign-link-btn"
                onClick={() => {
                  navigator.clipboard.writeText(signLinkUrl);
                  toast.success("Link copied to clipboard");
                }}
                className="mt-2 flex items-center gap-1 text-xs text-[#032352] hover:underline"
              >
                <Copy className="w-3 h-3" /> Copy link
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-[#5B5C5F]">
              <span className="animate-spin">⟳</span> Generating link…
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="sign-link-email" className="text-sm font-medium text-[#032352]">
                Recipient Email
              </Label>
              <Input
                id="sign-link-email"
                data-testid="sign-link-email-input"
                type="email"
                value={signLinkEmail}
                onChange={(e) => setSignLinkEmail(e.target.value)}
                placeholder="customer@example.com"
                className="mt-1"
              />
              {selectedInv?.customer_email && signLinkEmail !== selectedInv.customer_email && (
                <button
                  className="text-xs text-[#032352] underline mt-1"
                  onClick={() => setSignLinkEmail(selectedInv.customer_email)}
                >
                  Use {selectedInv.customer_email}
                </button>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setSignLinkOpen(false)} disabled={sendingSignLink} data-testid="sign-link-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleSendSignLink}
                disabled={sendingSignLink || !signLinkEmail.trim() || signLinkAppUrlMissing}
                data-testid="sign-link-send-btn"
                className="bg-[#032352] hover:bg-[#032352]/90"
              >
                <Link2 className="w-4 h-4 mr-2" />
                {sendingSignLink ? "Sending…" : "Send Sign Link"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Post-Sign Invoice Copy Prompt ──────────────────────────────── */}
      <Dialog open={postSignPromptOpen} onOpenChange={(o) => { if (!o) setPostSignPromptOpen(false); }}>
        <DialogContent className="max-w-sm" data-testid="post-sign-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#032352] font-heading">Send Invoice Copy?</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-[#5B5C5F] mb-5">
              Would you like a copy of invoice{" "}
              <strong>{postSignInv?.invoice_number}</strong> sent to the customer?
            </p>

            {postSignChoice === "" && (
              <div className="flex flex-col gap-2.5">
                {postSignInv?.customer_email && (
                  <Button data-testid="post-sign-yes-btn"
                    className="w-full bg-[#032352] hover:bg-[#04347A]"
                    onClick={() => {
                      setAutoInvoiceToEmail(postSignInv.customer_email);
                      setAutoInvoiceEmailOpen(true);
                    }}>
                    Yes — send to {postSignInv.customer_email}
                  </Button>
                )}
                <Button variant="outline" data-testid="post-sign-other-btn"
                  className="w-full border-[#032352] text-[#032352]"
                  onClick={() => setPostSignChoice("other")}>
                  Send to a different email
                </Button>
                <Button variant="outline" data-testid="post-sign-no-btn"
                  className="w-full text-[#5B5C5F]"
                  onClick={() => setPostSignPromptOpen(false)}>
                  No thanks
                </Button>
              </div>
            )}

            {postSignChoice === "other" && (
              <div className="flex flex-col gap-3">
                <p className="text-xs text-[#5B5C5F]">Enter recipient email:</p>
                <Input data-testid="post-sign-alt-email"
                  type="email" value={postSignAltEmail}
                  onChange={(e) => setPostSignAltEmail(e.target.value)}
                  placeholder="customer@example.com" autoFocus />
                <Button className="w-full bg-[#032352] hover:bg-[#04347A]"
                  disabled={!postSignAltEmail.trim()}
                  data-testid="post-sign-alt-send-btn"
                  onClick={() => {
                    setAutoInvoiceToEmail(postSignAltEmail.trim());
                    setAutoInvoiceEmailOpen(true);
                  }}>
                  Continue
                </Button>
                <button onClick={() => setPostSignChoice("")} className="text-xs text-[#5B5C5F] underline text-center">
                  Back
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Admin: Auto Invoice EmailPreviewModal ──────────────────────── */}
      {autoInvoiceEmailOpen && (
        <EmailPreviewModal
          open={autoInvoiceEmailOpen}
          onClose={() => setAutoInvoiceEmailOpen(false)}
          title={`Send Invoice — ${postSignInv?.invoice_number}`}
          toEmail={autoInvoiceToEmail}
          docType="auto_invoice"
          invoice={postSignInv}
          onSend={handleAutoInvoiceSend}
          sending={autoInvoiceSending}
        />
      )}
    </div>
  );
}
